import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { api, eur, errMsg, addMonths, toISO } from "@/lib/api";
import { toast } from "sonner";

export default function BookingForm({ listing, initial, onDone }) {
  const { t, pick } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const minM = Math.max(1, listing.min_months || 1);
  const [f, setF] = useState({ check_in: initial.check_in || "", check_out: initial.check_out || "", guests: initial.guests || 1, extras: [], message: "" });
  const [q, setQ] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const today = toISO(new Date());

  useEffect(() => {
    if (!f.check_in || !f.check_out) return setQ(null);
    const id = setTimeout(() => {
      api.post("/bookings/quote", { listing_id: listing.listing_id, check_in: f.check_in, check_out: f.check_out, extras: f.extras })
        .then((r) => { setQ(r.data); setErr(""); })
        .catch((e) => { setQ(null); setErr(errMsg(e)); });
    }, 250);
    return () => clearTimeout(id);
  }, [f.check_in, f.check_out, f.extras, listing.listing_id]);

  const setIn = (v) => setF((p) => ({ ...p, check_in: v, check_out: !p.check_out || p.check_out < addMonths(v, minM) ? addMonths(v, minM) : p.check_out }));
  const toggleExtra = (id) => setF((p) => ({ ...p, extras: p.extras.includes(id) ? p.extras.filter((x) => x !== id) : [...p.extras, id] }));

  const submit = async () => {
    if (!user) return nav(`/login?next=${encodeURIComponent(loc.pathname)}`);
    if (user.role !== "tenant") return toast.error(t("only_tenants"));
    setBusy(true);
    try {
      await api.post("/bookings", { listing_id: listing.listing_id, ...f, guests: Number(f.guests) });
      toast.success(t("request_sent"));
      onDone?.();
      nav("/my-bookings");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const inp = "h-12 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold outline-none focus:border-[#FF6B00]";
  return (
    <div className="space-y-4" data-testid="booking-form">
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("check_in")}
          <input type="date" min={listing.available_from > today ? listing.available_from : today} value={f.check_in} onChange={(e) => setIn(e.target.value)} className={`${inp} mt-1`} data-testid="booking-checkin-input" /></label>
        <label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("check_out")}
          <input type="date" min={f.check_in ? addMonths(f.check_in, minM) : today} value={f.check_out} onChange={(e) => setF({ ...f, check_out: e.target.value })} className={`${inp} mt-1`} data-testid="booking-checkout-input" /></label>
      </div>
      <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">{t("guests")}
        <input type="number" min="1" max={listing.max_guests} value={f.guests} onChange={(e) => setF({ ...f, guests: e.target.value })} className={`${inp} mt-1`} data-testid="booking-guests-input" /></label>
      {listing.extras?.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("extras")}</p>
          {listing.extras.map((e) => (
            <label key={e.extra_id} className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 px-3 min-h-[48px] text-sm">
              <span className="flex items-center gap-2"><Checkbox checked={f.extras.includes(e.extra_id)} onCheckedChange={() => toggleExtra(e.extra_id)} data-testid={`booking-extra-${e.extra_id}`} />{pick(e, "name")}</span>
              <span className="font-semibold text-[#142B5C]">{eur(e.price)} <span className="text-xs text-slate-500">{e.unit === "once" ? t("once") : t("monthly")}</span></span>
            </label>
          ))}
        </div>
      )}
      <Textarea placeholder={t("message_ph")} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className="rounded-xl" data-testid="booking-message-input" />
      {err && <p className="text-sm text-red-600" data-testid="booking-error">{err}</p>}
      {q ? (
        <div className="rounded-xl bg-slate-50 p-4 text-sm space-y-2" data-testid="booking-quote">
          <div className="flex justify-between"><span>{t("rent_line", { p: eur(q.price_month_public), m: q.months })}</span><span className="font-semibold">{eur(q.rent_public)}</span></div>
          {q.extras.map((e) => <div key={e.extra_id} className="flex justify-between text-slate-600"><span>{pick(e, "name")}</span><span>{eur(e.total)}</span></div>)}
          <div className="flex justify-between border-t pt-2 text-base font-extrabold text-[#0B192C]"><span>{t("total")}</span><span data-testid="booking-total">{eur(q.total_tenant)}</span></div>
          <p className="text-xs text-slate-500">{t("fee_note")}</p>
        </div>
      ) : (
        !err && <p className="text-sm text-slate-500">{t("choose_dates")}</p>
      )}
      <button onClick={submit} disabled={busy || (user && !q)} className="h-13 min-h-[52px] w-full rounded-full bg-[#FF6B00] font-bold text-white hover:bg-[#E05A00] disabled:opacity-50 transition-colors" data-testid="booking-submit-btn">
        {!user ? t("login_to_book") : t("send_request")}
      </button>
    </div>
  );
}
